import {
  ClusterOutlined,
  EditOutlined,
  WarningOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { Button, Tabs, Tooltip, List, Avatar, Modal } from "antd";
import { Content } from "antd/lib/layout/layout";
import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import { GetPerfCycleTopLevelApi } from "../../api/perf-cycles/GetPerfCycleTopPlanApi";
import GetPlansDetailsApi from "../../api/plans/GetPlanDetailsApi";
import DashboardBreadcrumb from "../../components/layout/DashboardBreadcrumb";
import CascadePlanModal from "../../components/modal/CascadePlanModal";
import EditTopPlanModal from "../../components/modal/edit/EditTopPlanModal";
import SwitchTopPlanModal from "../../components/modal/SwitchTopPlanModal";
import PageLoader from "../../components/sub/PageLoader";
import TopPlanKpi from "../../components/top-plan/TopPlanKpi";
import TopPlanOverview from "../../components/top-plan/TopPlanOverview";
import TopPlanPlans from "../../components/top-plan/TopPlanPlans";
import TopPlanTasks from "../../components/top-plan/TopPlanTasks";
import PlanTimesheetSummary from "../../components/top-plan/PlanTimesheetSummary";
import PlansTree from "../../components/tree/PlansTree";
import SubTargetsComponent from "../../components/SubTargetsComponent";
import { permissionAbility } from "../../helpers/permissionHelpers";
import { getPluralString } from "../../helpers/wordHelpers";
import { postPlanDiscussion } from '../../api/plans/PostPlanDiscussionApi';
import { getPlanDiscussions } from '../../api/plans/GetPlanDiscussionApi';
import { uploadDiscussionAttachment } from '../../api/UploadDiscussionAttachmentApi';
import { usePermissions } from "../../hooks/usePermissions";
import DiscussionHistoryModal from "../../components/Plans/DiscussionHistoryModal";

const { TabPane } = Tabs;

// Helper to get API URL and headers
const API_URL = process.env.REACT_APP_API_URL + 'v1/plans/';
function getAuthHeaders() {
  return {
    'Content-Type': 'application/json',
    accept: 'application/json',
    Authorization: 'Bearer ' + localStorage.getItem('authToken'),
  };
}

// Add this helper for a styled file input button
function StyledFileInput({ onChange, disabled, id }) {
  return (
    <label
      htmlFor={id}
      style={{
        display: 'inline-block',
        padding: '4px 16px',
        border: '2px solid #2563eb', // blue-600
        borderRadius: '6px',
        background: '#fff',
        color: '#000',
        fontWeight: 500,
        fontSize: 14,
        cursor: disabled ? 'not-allowed' : 'pointer',
        marginTop: 8,
        marginBottom: 4,
        transition: 'border-color 0.2s',
      }}
      className="hover:border-blue-700"
    >
      Attach file
      <input
        id={id}
        type="file"
        onChange={onChange}
        disabled={disabled}
        style={{ display: 'none' }}
      />
    </label>
  );
}

// Circular progress component
function CircularProgress({ progress, size = 40 }) {
  const radius = (size - 4) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth="2"
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#2563eb"
          strokeWidth="2"
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
        />
      </svg>
      {/* Progress text */}
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontSize: '10px',
        fontWeight: 'bold',
        color: '#2563eb'
      }}>
        {Math.round(progress)}%
      </div>
    </div>
  );
}

// Bulleted list icon button component
function BulletIconButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title="Add bullet point"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 50,
        height: 32,
        border: 'none', // Remove outline
        borderRadius: 6,
        background: '#fff',
        color: '#2563eb',
        marginLeft: 0,
        marginRight: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 0,
      }}
      className="hover:border-blue-700"
    >
      <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="4" cy="5" r="1.5" fill="#2563eb" />
        <rect x="7" y="4.25" width="9" height="1.5" rx="0.75" fill="#2563eb" />
        <circle cx="4" cy="10" r="1.5" fill="#2563eb" />
        <rect x="7" y="9.25" width="9" height="1.5" rx="0.75" fill="#2563eb" />
        <circle cx="4" cy="15" r="1.5" fill="#2563eb" />
        <rect x="7" y="14.25" width="9" height="1.5" rx="0.75" fill="#2563eb" />
      </svg>
    </button>
  );
}

// Helper to count words
function countWords(str) {
  return str.trim().split(/\s+/).filter(Boolean).length;
}
const WORD_LIMIT = 100;

// Helper to format message with bullet points and line breaks
function formatMessage(message) {
  if (!message) return '';
  
  // Split by lines and process each line
  const lines = message.split('\n');
  const formattedLines = lines.map((line, index) => {
    // Check if line starts with bullet point
    if (line.trim().startsWith('•')) {
      return (
        <div key={index} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '4px' }}>
          <span style={{ marginRight: '8px', color: '#666' }}>•</span>
          <span>{line.trim().substring(1).trim()}</span>
        </div>
      );
    }
    // Regular line
    return <div key={index} style={{ marginBottom: '4px' }}>{line}</div>;
  });
  
  return <div>{formattedLines}</div>;
}

// Helper to insert a bullet point at the cursor position
function insertPoint(text, setText, ref) {
  const bullet = '\u2022 ';
  if (ref && ref.current) {
    const textarea = ref.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = text.substring(0, start);
    const after = text.substring(end);
    const newText = before + (before && before[before.length - 1] !== '\n' ? '\n' : '') + bullet + after;
    setText(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = before.length + bullet.length + ((before && before[before.length - 1] !== '\n') ? 1 : 0);
    }, 0);
  } else {
    setText(text + (text && text[text.length - 1] !== '\n' ? '\n' : '') + bullet);
  }
}

function TopPlan({planType}) {
  const [topPlan, setTopPlan] = useState();
  const [canStart, setCanStart] = useState(false);
  const [canShow, setCanShow] = useState(false);
  const [planDetails, setPlanDetails] = useState();
  const [editModal, setEditModal] = useState(false);
  const [cascade, setCascade] = useState(false);
  const [switchModal, setSwitchModal] = useState(false);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const topPlanState = useSelector((state) => state.topPlanState.value);
  const positionState = useSelector((state) => state.positionState.value);
  const userState = useSelector((state) => state.userState.value);
  const { hasSubtargetsPermission } = usePermissions();

  const userLevel = positionState?.userRole?.name;
  const proHeight = document.body.clientHeight;
  let curLocation = useLocation();
  const { id } = useParams();
  
  // Discussion tab state variables
  const [discussionMessage, setDiscussionMessage] = useState('');
  const [posting, setPosting] = useState(false);
  const [discussions, setDiscussions] = useState([]);
  const [loadingDiscussions, setLoadingDiscussions] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingMessage, setEditingMessage] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const discussionRef = React.useRef();
  const replyRefs = React.useRef({});
  const [enlargedImage, setEnlargedImage] = useState(null);
  
  // Attachment related state
  const [editingAttachments, setEditingAttachments] = useState([]);
  const [editingAttachmentPreviews, setEditingAttachmentPreviews] = useState([]);
  const [discussionAttachments, setDiscussionAttachments] = useState([]);
  const [discussionAttachmentPreviews, setDiscussionAttachmentPreviews] = useState([]);
  const [replyAttachments, setReplyAttachments] = useState([]);
  
  // ====== DISCUSSION EDIT HISTORY FEATURE START ======
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [selectedDiscussionId, setSelectedDiscussionId] = useState(null);
  // ====== DISCUSSION EDIT HISTORY FEATURE END ======
  const [replyAttachmentPreviews, setReplyAttachmentPreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [postingReply, setPostingReply] = useState(false);

  const getTopPlanDetails = useCallback(async () => {
    const apiResponse = await GetPerfCycleTopLevelApi(perfCycleState.id);
    if (apiResponse.success) {
      setTopPlan(apiResponse.data);
    }
  }, [perfCycleState.id]);

  const getPlanData = useCallback(async () => {
    const apiResponse = await GetPlansDetailsApi(id, positionState?.id);
    if (apiResponse.message === "success") {
      // Ensure isPercentageUpdate is included in the plan data
      const enhancedPlanData = {
        ...apiResponse.plan,
        isPercentageUpdate: apiResponse.plan?.isPercentageUpdate || false
      };
      
      setPlanDetails(enhancedPlanData);
      setCanShow(true);
    } else {
      console.error('❌ Failed to load plan data:', apiResponse);
    }
    setCanStart(true);
  }, [id, positionState?.id]);

  const fetchDiscussions = useCallback(async () => {
    setLoadingDiscussions(true);
    const res = await getPlanDiscussions(id);
    setLoadingDiscussions(false);
    if (Array.isArray(res)) {
      setDiscussions(res);
    } else {
      setDiscussions([]);
    }
  }, [id]);

  useEffect(() => {
    getTopPlanDetails();
    getPlanData();
    fetchDiscussions();
  }, [curLocation, perfCycleState, getTopPlanDetails, getPlanData, fetchDiscussions]);

  const right = (
    <>
      {permissionAbility(userLevel, "plans", "cascade") && (
        <Tooltip title={planDetails?.cascadeFromSplitPlans ? "Cascade disabled - This plan uses split plans for progress" : "Cascade"}>
          <Button 
            onClick={() => setCascade(true)} 
            className="mr-3"
            disabled={planDetails?.cascadeFromSplitPlans}
          >
            <ClusterOutlined />
          </Button>
        </Tooltip>
      )}
      {permissionAbility(userLevel, "plans", "edit") && (
        <Tooltip title={"Edit"}>
          <Button onClick={() => {
            getPlanData(); // Refresh plan data to ensure dropdown values are loaded
            setEditModal(true);
          }}>
            <EditOutlined />
          </Button>
        </Tooltip>
      )}
    </>
  );


  const left = (
    <>
      <div className="flex items-baseline">
        {planType === "my-plans" ? (
          <Link to={`/my-plans`}>
            <div className="text-sm pri-font text-gray-400 hover:text-gray-500">
              {getPluralString(topPlanState)}
            </div>
          </Link>
        ) : (
          <Link to={`/top-plans`}>
            <div className="text-sm pri-font text-gray-400 hover:text-gray-500">
              {getPluralString(topPlanState)}
            </div>
          </Link>
        )}
        <div className="mx-1 text-lg">/</div>
        <div className="text-lg pri-font max-w-xs truncate" style={{
          opacity: (planDetails?.isActive === 0 || planDetails?.isActive === false) ? 0.5 : 1
        }}>
          {planDetails?.title || "..."}
          {(planDetails?.isActive === 0 || planDetails?.isActive === false) && (
            <span className="ml-2 text-xs text-yellow-600 font-normal">(Inactive)</span>
          )}
        </div>
      </div>
    </>
  );

  // Recursive message renderer
  function renderMessage(msg, level = 0) {
    const isCurrentUser =
      msg.user?.username === userState?.username ||
      msg.username === userState?.username;



    return (
      <div key={msg.id} style={{ marginLeft: level * 32, marginTop: 8 }}>
        <List.Item className="px-0">
          <List.Item.Meta
            avatar={
              msg.user?.imageUrl ? (
                <Avatar src={msg.user.imageUrl} />
              ) : (
                <Avatar>
                  {((msg.user?.firstName?.[0] || "U") + (msg.user?.lastName?.[0] || "")).toUpperCase()}
                </Avatar>
              )
            }
            title={
              <span className="font-semibold pri-font">
                {msg.user?.firstName || "User"}
                {msg.user?.lastName ? ` ${msg.user.lastName}` : ""}
              </span>
            }
            description={
              editingId === msg.id ? (
                <>
                  <textarea
                    className="w-full border rounded p-2 mb-2"
                    rows={2}
                    value={editingMessage}
                    onChange={e => setEditingMessage(e.target.value)}
                    autoFocus
                    ref={(el) => {
                      if (el) {
                        el.focus();
                        el.setSelectionRange(el.value.length, el.value.length);
                      }
                    }}
                  />
                  <div className="flex items-center gap-2 mt-1 mb-1">
                    <StyledFileInput onChange={handleEditingFileChange} disabled={false} id="editing-file" />
                    <BulletIconButton onClick={() => insertPoint(editingMessage, setEditingMessage, { current: null })} disabled={false} />
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {/* Upload progress indicators */}
                    {Object.entries(uploadProgress).map(([fileId, progress]) => (
                      <div key={fileId} className="border rounded p-1 text-xs flex items-center">
                        <CircularProgress progress={progress} size={32} />
                        <span className="ml-2 text-gray-500">Uploading...</span>
                      </div>
                    ))}
                    {/* Uploaded files */}
                    {editingAttachmentPreviews.map((file, idx) => (
                      <div key={idx} className="border rounded p-1 text-xs flex items-center relative">
                        {file.type.startsWith('image') ? (
                          <img
                            src={file.url}
                            alt={file.name}
                            style={{ width: 40, height: 40, objectFit: 'cover', marginRight: 4, cursor: 'pointer' }}
                            onClick={() => setEnlargedImage(file.url)}
                          />
                        ) : (
                          <a href={file.url} target="_blank" rel="noopener noreferrer" className="mr-2 underline">
                            {file.name}
                          </a>
                        )}
                        <span
                          style={{ cursor: 'pointer', color: '#ef4444', marginLeft: 4 }}
                          onClick={() => {
                            setEditingAttachmentPreviews(prev => prev.filter((_, i) => i !== idx));
                            setEditingAttachments(prev => prev.filter((_, i) => i !== idx));
                          }}
                        >
                          ×
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      disabled={!editingMessage.trim() || editingMessage.trim() === msg.message.trim()}
                      onClick={async () => {
                        if (!editingMessage.trim()) return;
                        // Check if message actually changed
                        if (editingMessage.trim() === msg.message.trim()) {
                          alert("No changes made to the message");
                          return;
                        }
                        const username = userState?.username;
                        
                        const response = await fetch(
                          `${API_URL}${id}/discussions/${msg.id}`,
                          {
                            method: "PATCH",
                            headers: getAuthHeaders(),
                            body: JSON.stringify({
                              userId: username,
                              message: editingMessage.trim(),
                              attachments: editingAttachmentPreviews.map(file => ({
                                fileName: file.name,
                                filePath: file.url,
                                mimeType: file.type
                              })),
                            }),
                          }
                        );
                        if (response.ok) {
                          await fetchDiscussions();
                          setEditingId(null);
                          setEditingMessage("");
                        } else {
                          const err = await response.json();
                          alert(err.message || "Failed to update message");
                        }
                      }}
                    >
                      Save
                    </button>
                    <button
                      className="px-3 py-1 bg-gray-300 rounded hover:bg-gray-400"
                      onClick={() => {
                        setEditingId(null);
                        setEditingMessage("");
                        setEditingAttachments([]);
                        setEditingAttachmentPreviews([]);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-1">{formatMessage(msg.message)}</div>
                  {msg.attachments && msg.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {msg.attachments.map((file, idx) => (
                        <div key={file.id} className="border rounded p-1 text-xs flex items-center">
                          {file.type && file.type.startsWith('image') ? (
                            <img
                              src={file.url}
                              alt={file.name}
                              style={{ width: 40, height: 40, objectFit: 'cover', marginRight: 4, cursor: 'pointer' }}
                              onClick={() => setEnlargedImage(file.url)}
                            />
                          ) : (
                            <a href={file.url} target="_blank" rel="noopener noreferrer" className="mr-2 underline">
                              {file.name}
                            </a>
                          )}
                          <a
                            href={file.url}
                            download={file.name}
                            className="ml-2 text-blue-600 hover:underline"
                            title="Download"
                          >
                            Download
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="text-xs text-gray-400">
                    {new Date(msg.createdOn).toLocaleString()}
                  </div>
                  <div className="flex gap-2 mt-2">
                    {isCurrentUser && (
                      <>
                        <button
                          className="px-2 py-1 text-xs bg-yellow-400 rounded hover:bg-yellow-500"
                          onClick={() => {
                            setEditingId(msg.id);
                            setEditingMessage(msg.message);
                            setEditingAttachments(msg.attachments ? msg.attachments.map(att => att.id) : []);
                            setEditingAttachmentPreviews(msg.attachments ? msg.attachments.map(att => ({
                              url: att.url,
                              name: att.name,
                              type: att.type
                            })) : []);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                          onClick={() => {
                            Modal.confirm({
                              title: "Delete Message",
                              content: "Are you sure you want to delete this message?",
                              okText: "Delete",
                              okType: "danger",
                              cancelText: "Cancel",
                              onOk: async () => {
                                const username = userState?.username;
                                
                                const response = await fetch(
                                  `${API_URL}${id}/discussions/${msg.id}`,
                                  {
                                    method: "DELETE",
                                    headers: getAuthHeaders(),
                                    body: JSON.stringify({
                                      userId: username,
                                    }),
                                  }
                                );
                                if (response.ok) {
                                  await fetchDiscussions();
                                } else {
                                  const err = await response.json();
                                  alert(err.message || "Failed to delete message");
                                }
                              },
                            });
                          }}
                        >
                          Delete
                        </button>
                      </>
                    )}
                    <button
                      className="px-2 py-1 text-xs bg-blue-200 rounded hover:bg-blue-300"
                      onClick={() => {
                        setReplyingTo(msg.id);
                        setReplyMessage("");
                      }}
                    >
                      Reply
                    </button>
                    {/* ====== DISCUSSION EDIT HISTORY FEATURE START ====== */}
                    <button
                      className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300 flex items-center gap-1"
                      onClick={() => {
                        setSelectedDiscussionId(msg.id);
                        setHistoryModalVisible(true);
                      }}
                      title="View edit history"
                    >
                      <HistoryOutlined style={{ fontSize: '12px' }} />
                    </button>
                    {/* ====== DISCUSSION EDIT HISTORY FEATURE END ====== */}
                  </div>
                  {replyingTo === msg.id && (
                    <div className="mt-2 flex flex-col gap-2 w-2/3 max-w-xs">
                      <div style={{ position: 'relative' }}>
                        <textarea
                          ref={el => replyRefs.current[msg.id] = el}
                          className="border rounded p-1 text-sm resize-none w-full"
                          style={{ minHeight: 32, maxHeight: 60, paddingRight: 60 }}
                          value={replyMessage}
                          onChange={e => setReplyMessage(e.target.value)}
                          placeholder="Write a reply..."
                          disabled={postingReply}
                          rows={2}
                        />
                        <span
                          style={{
                            position: 'absolute',
                            bottom: 4,
                            right: 8,
                            fontSize: 12,
                            color: countWords(replyMessage) > WORD_LIMIT ? '#ef4444' : '#6b7280',
                            pointerEvents: 'none',
                            background: 'rgba(255,255,255,0.8)',
                            padding: '0 4px',
                            borderRadius: 4,
                          }}
                        >
                          {countWords(replyMessage)} / {WORD_LIMIT} words
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 mb-1">
                        <StyledFileInput onChange={e => handleReplyFileChange(e, msg.id)} disabled={postingReply} id={`reply-file-${msg.id}`} />
                        <BulletIconButton onClick={() => insertPoint(replyMessage, setReplyMessage, { current: replyRefs.current[msg.id] })} disabled={postingReply} />
                      </div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {/* Upload progress indicators */}
                        {Object.entries(uploadProgress).map(([fileId, progress]) => (
                          <div key={fileId} className="border rounded p-1 text-xs flex items-center">
                            <CircularProgress progress={progress} size={32} />
                            <span className="ml-2 text-gray-500">Uploading...</span>
                          </div>
                        ))}
                        {/* Uploaded files */}
                        {(replyAttachmentPreviews[msg.id] || []).map((file, idx) => (
                          <div key={idx} className="border rounded p-1 text-xs flex items-center">
                            {file.type.startsWith('image') ? (
                              <img 
                                src={file.url} 
                                alt={file.name} 
                                style={{ width: 40, height: 40, objectFit: 'cover', marginRight: 4, cursor: 'pointer' }}
                                onClick={() => setEnlargedImage(file.url)}
                              />
                            ) : (
                              <span>{file.name}</span>
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <button
                          className="flex-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          disabled={postingReply || !replyMessage.trim() || countWords(replyMessage) > WORD_LIMIT}
                          onClick={async () => {
                            if (!replyMessage.trim() || countWords(replyMessage) > WORD_LIMIT) return;
                            setPostingReply(true);
                            const res = await postPlanDiscussion({
                              planId: id,
                              message: replyMessage.trim(),
                              parentId: msg.id,
                              attachments: (replyAttachmentPreviews[msg.id] || []).map(file => ({
                                fileName: file.name,
                                filePath: file.url,
                                mimeType: file.type
                              })),
                            });
                            setPostingReply(false);
                            if (res && !res.error) {
                              setReplyingTo(null);
                              setReplyMessage("");
                              setReplyAttachments(prev => ({ ...prev, [msg.id]: [] }));
                              setReplyAttachmentPreviews(prev => ({ ...prev, [msg.id]: [] }));
                              await fetchDiscussions();
                            } else {
                              alert(res?.error || 'Failed to post reply');
                            }
                          }}
                        >
                          {postingReply ? 'Replying...' : 'Reply'}
                        </button>
                        <button
                          className="flex-1 px-2 py-1 text-xs bg-gray-300 rounded hover:bg-gray-400"
                          onClick={() => setReplyingTo(null)}
                          disabled={postingReply}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Render replies recursively */}
                  {msg.replies && msg.replies.length > 0 && (
                    <div className="mt-2">
                      {msg.replies.map(reply => renderMessage(reply, level + 1))}
                    </div>
                  )}
                </>
              )
            }
          />
        </List.Item>
      </div>
    );
  }

  // Helper for file input change
  async function handleDiscussionFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    
    const fileId = Date.now() + Math.random();
    setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
    
    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        const current = prev[fileId] || 0;
        if (current >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return { ...prev, [fileId]: current + 10 };
      });
    }, 100);
    
    const res = await uploadDiscussionAttachment(file);
    clearInterval(progressInterval);
    
    if (res && res.url) {
      setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
      setTimeout(() => {
        setDiscussionAttachments(prev => [...prev, res.id]);
        setDiscussionAttachmentPreviews(prev => [...prev, { 
          url: res.url, 
          name: file.name, 
          type: file.type,
          id: fileId 
        }]);
        setUploadProgress(prev => {
          const { [fileId]: removed, ...rest } = prev;
          return rest;
        });
      }, 500);
    } else {
      setUploadProgress(prev => {
        const { [fileId]: removed, ...rest } = prev;
        return rest;
      });
    }
    e.target.value = '';
  }
  
  async function handleReplyFileChange(e, msgId) {
    const file = e.target.files[0];
    if (!file) return;
    
    const fileId = Date.now() + Math.random();
    setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
    
    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        const current = prev[fileId] || 0;
        if (current >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return { ...prev, [fileId]: current + 10 };
      });
    }, 100);
    
    const res = await uploadDiscussionAttachment(file);
    clearInterval(progressInterval);
    
    if (res && res.url) {
      setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
      setTimeout(() => {
        setReplyAttachments(prev => ({ ...prev, [msgId]: [...(prev[msgId] || []), res.id] }));
        setReplyAttachmentPreviews(prev => ({ ...prev, [msgId]: [...(prev[msgId] || []), { 
          url: res.url, 
          name: file.name, 
          type: file.type,
          id: fileId 
        }] }));
        setUploadProgress(prev => {
          const { [fileId]: removed, ...rest } = prev;
          return rest;
        });
      }, 500);
    } else {
      setUploadProgress(prev => {
        const { [fileId]: removed, ...rest } = prev;
        return rest;
      });
    }
    e.target.value = '';
  }

  async function handleEditingFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    
    const fileId = Date.now() + Math.random();
    setUploadProgress(prev => ({ ...prev, [fileId]: 0 }));
    
    // Simulate upload progress
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        const current = prev[fileId] || 0;
        if (current >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return { ...prev, [fileId]: current + 10 };
      });
    }, 100);
    
    const res = await uploadDiscussionAttachment(file);
    clearInterval(progressInterval);
    
    if (res && res.url) {
      setUploadProgress(prev => ({ ...prev, [fileId]: 100 }));
      setTimeout(() => {
        setEditingAttachments(prev => [...prev, res.id]);
        setEditingAttachmentPreviews(prev => [...prev, { 
          url: res.url, 
          name: file.name, 
          type: file.type,
          id: fileId 
        }]);
        setUploadProgress(prev => {
          const { [fileId]: removed, ...rest } = prev;
          return rest;
        });
      }, 500);
    } else {
      setUploadProgress(prev => {
        const { [fileId]: removed, ...rest } = prev;
        return rest;
      });
    }
    e.target.value = '';
  }
  

  return (
    <>
      <Content style={{ backgroundColor: "#fff" }}>
        {canStart ? (
          canShow ? (
            <>
              <DashboardBreadcrumb left={left} right={right} />
              <div
                style={{
                  height: proHeight - 122,
                  overflow: "auto",
                }}
                className="custom-scroll"
              >
                <Tabs defaultActiveKey="1" centered>
                  <TabPane
                    tab={<div className="w-32 text-center">Overview</div>}
                    key="1"
                  >
                    <div className="px-6">
                      <TopPlanOverview data={planDetails} />
                    </div>
                  </TabPane>
                  {topPlan?.kpi && (
                    <TabPane
                      tab={<div className="w-32 text-center">KPIs</div>}
                      key="2"
                    >
                      <div className="px-6">
                        <TopPlanKpi data={planDetails} />
                      </div>
                    </TabPane>
                  )}
                  {topPlan?.tasks && (
                    <TabPane
                      tab={<div className="w-32 text-center">Tasks</div>}
                      key="3"
                    >
                      <div className="px-6">
                        <TopPlanTasks data={planDetails} />
                      </div>
                    </TabPane>
                  )}
                  {(topPlan?.plansTab !== false) && (
                  <TabPane
                    tab={<div className="w-32 text-center">Plans</div>}
                    key="4"
                  >
                    <div className="px-6">
                      <TopPlanPlans 
                        data={planDetails} 
                        onParentRefresh={() => {
                          getPlanData();
                        }}
                      />
                    </div>
                  </TabPane>
                  )}
                  {(topPlan?.hierarchyTab !== false) && (
                  <TabPane
                    tab={<div className="w-32 text-center">Hierarchy</div>}
                    key="5"
                  >
                    <div className="px-6">
                      {planDetails?.hasChildren ? (
                        <PlansTree plan={planDetails?.planId} />
                      ) : (
                        <div
                          style={{ height: proHeight - 188 }}
                          className={"flex w-full items-center justify-center"}
                        >
                          <div className="text-center">
                            <div className="text-3xl text-gray-400">
                              <ClusterOutlined />
                            </div>
                            <div className="text-lg pri-font text-gray-400">
                              No child plans
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </TabPane>
                  )}
                  {topPlan?.timesheet && (
                    <TabPane
                      tab={<div className="w-32 text-center">Timesheets</div>}
                      key="6"
                    >
                      <PlanTimesheetSummary planId={planDetails?.planId} data={planDetails} />
                    </TabPane>
                  )}
                  {hasSubtargetsPermission && !planDetails?.hasChildren && (
                    <TabPane
                      tab={<div className="w-32 text-center">Sub-Targets</div>}
                      key="7"
                    >
                      <div className="px-6">
                        <SubTargetsComponent planId={planDetails?.planId} planData={planDetails} />
                      </div>
                    </TabPane>
                  )}
                  <TabPane
                    tab={<div className="w-32 text-center">Discussion</div>}
                    key="8"
                  >
                    <div className="px-6">
                      <div className="mb-4">
                        <div style={{ position: 'relative' }}>
                          <textarea
                            ref={discussionRef}
                            className="w-full border rounded p-2"
                            rows={3}
                            placeholder="Type your message..."
                            value={discussionMessage}
                            onChange={e => setDiscussionMessage(e.target.value)}
                            disabled={posting}
                            style={{ paddingRight: 60 }}
                          />
                          <span
                            style={{
                              position: 'absolute',
                              bottom: 8,
                              right: 16,
                              fontSize: 12,
                              color: countWords(discussionMessage) > WORD_LIMIT ? '#ef4444' : '#6b7280',
                              pointerEvents: 'none',
                              background: 'rgba(255,255,255,0.8)',
                              padding: '0 4px',
                              borderRadius: 4,
                            }}
                          >
                            {countWords(discussionMessage)} / {WORD_LIMIT} words
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 mb-1">
                          <StyledFileInput onChange={handleDiscussionFileChange} disabled={posting} id="discussion-file" />
                          <BulletIconButton onClick={() => insertPoint(discussionMessage, setDiscussionMessage, discussionRef)} disabled={posting} />
                        </div>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {/* Upload progress indicators */}
                          {Object.entries(uploadProgress).map(([fileId, progress]) => (
                            <div key={fileId} className="border rounded p-1 text-xs flex items-center">
                              <CircularProgress progress={progress} size={32} />
                              <span className="ml-2 text-gray-500">Uploading...</span>
                            </div>
                          ))}
                          {/* Uploaded files */}
                          {discussionAttachmentPreviews.map((file, idx) => (
                            <div key={idx} className="border rounded p-1 text-xs flex items-center relative">
                              {file.type.startsWith('image') ? (
                                <img
                                  src={file.url}
                                  alt={file.name}
                                  style={{ width: 40, height: 40, objectFit: 'cover', marginRight: 4, cursor: 'pointer' }}
                                  onClick={() => setEnlargedImage(file.url)}
                                />
                              ) : (
                                <a href={file.url} target="_blank" rel="noopener noreferrer" className="mr-2 underline">
                                  {file.name}
                                </a>
                              )}
                              <span
                                style={{ cursor: 'pointer', color: '#ef4444', marginLeft: 4 }}
                                onClick={() => {
                                  setDiscussionAttachmentPreviews(prev => prev.filter((_, i) => i !== idx));
                                  setDiscussionAttachments(prev => prev.filter((_, i) => i !== idx));
                                }}
                              >
                                ×
                              </span>
                            </div>
                          ))}
                        </div>
                        <Modal
                          open={enlargedImage !== null}
                          onCancel={() => setEnlargedImage(null)}
                          footer={null}
                          width="auto"
                          centered
                          style={{ maxWidth: '90vw', maxHeight: '90vh' }}
                          bodyStyle={{ padding: 0, textAlign: 'center' }}
                        >
                          {enlargedImage && (
                            <img
                              src={enlargedImage}
                              alt="Enlarged view"
                              style={{
                                maxWidth: '100%',
                                maxHeight: '80vh',
                                objectFit: 'contain',
                                cursor: 'pointer'
                              }}
                              onClick={() => setEnlargedImage(null)}
                            />
                          )}
                        </Modal>
                        <button
                          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                          disabled={posting || !discussionMessage.trim() || !id || countWords(discussionMessage) > WORD_LIMIT}
                          onClick={async () => {
                            if (!discussionMessage.trim() || !id || countWords(discussionMessage) > WORD_LIMIT) return;
                            setPosting(true);
                            const res = await postPlanDiscussion({
                              planId: id,
                              message: discussionMessage.trim(),
                              attachments: discussionAttachmentPreviews.map(file => ({
                                fileName: file.name,
                                filePath: file.url,
                                mimeType: file.type
                              })),
                            });
                            setPosting(false);
                            if (res && res.id) {
                              setDiscussionMessage('');
                              setDiscussionAttachments([]);
                              setDiscussionAttachmentPreviews([]);
                              await fetchDiscussions();
                            } else {
                              alert(res?.error || 'Failed to post message');
                            }
                          }}
                        >
                          {posting ? 'Posting...' : 'Post'}
                        </button>
                      </div>
                      <div>
                        {loadingDiscussions ? (
                          <div>Loading discussions...</div>
                        ) : discussions.length === 0 ? (
                          <div className="text-gray-400">No messages yet.</div>
                        ) : (
                          <div>
                            {discussions.map(msg => renderMessage(msg))}
                          </div>
                        )}
                      </div>
                    </div>
                  </TabPane>
                </Tabs>
              </div>
            </>
          ) : (
            <div
              style={{ height: proHeight - 64 }}
              className={"flex w-full items-center justify-center"}
            >
              <div className="text-center">
                <div className="text-3xl text-gray-400">
                  <WarningOutlined />
                </div>
                <div className="text-lg pri-font text-gray-400">
                  No {topPlanState} Found
                </div>
              </div>
            </div>
          )
        ) : (
          <PageLoader />
        )}
      </Content>
      <SwitchTopPlanModal
        visible={switchModal}
        handleCancel={() => setSwitchModal(false)}
      />
      <CascadePlanModal
        visible={cascade}
        handleCancel={() => setCascade(false)}
        plan={planDetails}
      />
      <EditTopPlanModal
        visible={editModal}
        handleCancel={() => setEditModal(false)}
        editPlan={planDetails}
        handleOk={(updatedPlan) => {
          // Update local plan details without causing navigation
          if (updatedPlan) {
            setPlanDetails(updatedPlan);
          } else {
            // Fallback to refresh if no updated data is provided
            getPlanData();
          }
          setEditModal(false);
        }}
      />
      {/* ====== DISCUSSION EDIT HISTORY FEATURE START ====== */}
      <DiscussionHistoryModal
        visible={historyModalVisible}
        onClose={() => {
          setHistoryModalVisible(false);
          setSelectedDiscussionId(null);
        }}
        discussionId={selectedDiscussionId}
      />
      {/* ====== DISCUSSION EDIT HISTORY FEATURE END ====== */}
    </>
  );
}

export default TopPlan;
